import * as React from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Activity, Bell, Building2, ClipboardList, ExternalLink, Flag, Gauge, Globe, LifeBuoy, Receipt, LogOut, Megaphone, Menu, Package, ScrollText, Settings, Ticket, X } from 'lucide-react';
import { usePlatformGeneral } from '../hooks/use-platform-general';
import { useAuthStore } from '../auth/auth.store';
import { useLiveUpdates } from '../realtime/use-live-updates';
import { useDateFormatSetting } from '../lib/use-date-format';
import { useBranding, useBrandAssets, useBrandLogoHeight } from '../hooks/use-branding';
import { Avatar, Badge, Button, cn } from '../ui/primitives';
import { FullscreenToggle, ThemeToggle } from '../theme/ThemeControls';
import { PlatformInboxPanel, usePlatformInboxUnread } from '../ui/platform-inbox';
import { useLocaleSetting } from '../../i18n/use-locale';
import { LanguageSwitch } from '../../i18n/LanguageSwitch';
import { CurrencySwitch, useCurrencyStore } from '../ui/currency-switch';
import { useBaseCurrency } from '../hooks/use-base-currency';
import { t } from '../../i18n';

/**
 * Media City's own console.
 *
 * Visually distinct from the society shell on purpose — a dark rail. Someone
 * with access to every society's data should never be in any doubt about which
 * product they are looking at.
 */

const NAV: { to: string; label: string; icon: React.ComponentType<{ className?: string }>; roles?: string[] }[] = [
  { to: '/platform', get label() { return t('Overview'); }, icon: Gauge },
  { to: '/platform/societies', get label() { return t('Societies'); }, icon: Building2 },
  { to: '/platform/plans', get label() { return t('Plans'); }, icon: Package, roles: ['SUPER_ADMIN'] },
  { to: '/platform/purchases', get label() { return t('Purchases'); }, icon: Receipt, roles: ['SUPER_ADMIN', 'PLATFORM_FINANCE'] },
  { to: '/platform/coupons', get label() { return t('Coupons'); }, icon: Ticket, roles: ['SUPER_ADMIN', 'PLATFORM_FINANCE'] },
  { to: '/platform/flags', get label() { return t('Feature flags'); }, icon: Flag, roles: ['SUPER_ADMIN'] },
  { to: '/platform/support', get label() { return t('Support'); }, icon: LifeBuoy },
  { to: '/platform/audit', get label() { return t('Audit log'); }, icon: ClipboardList, roles: ['SUPER_ADMIN'] },
  { to: '/platform/health', get label() { return t('System health'); }, icon: Activity },
  { to: '/platform/notifications', get label() { return t('Notifications'); }, icon: Megaphone },
  { to: '/platform/content', get label() { return t('Website'); }, icon: Globe, roles: ['SUPER_ADMIN'] },
  { to: '/platform/compliance', get label() { return t('Compliance'); }, icon: ScrollText, roles: ['SUPER_ADMIN'] },
  { to: '/platform/settings', get label() { return t('Settings'); }, icon: Settings, roles: ['SUPER_ADMIN'] },
];

export function PlatformShell(): React.JSX.Element {
  // Opens the socket and keeps the console in step with the server.
  useLiveUpdates();
  // Applies the platform-wide date format to every date in the app.
  useDateFormatSetting();
  useBranding('Platform console');
  const brand = useBrandAssets();
  const logoHeight = useBrandLogoHeight();
  // Subscribing here re-renders the shell and everything under it when the
  // currency changes. `formatMoney` reads the choice at render time, so without
  // a subscription the amounts kept their old currency until a page reload.
  useCurrencyStore((state) => state.code);

  // The platform console has no society, so its money is in the base currency —
  // what plans are priced in. Without this it fell back to INR, so the app bar
  // claimed INR while the prices beside it were converted from a USD base: the
  // label and the arithmetic disagreed.
  const base = useBaseCurrency();
  const setOwnCurrency = useCurrencyStore((state) => state.setOwn);
  React.useEffect(() => {
    if (base.code) setOwnCurrency(base.code);
  }, [base.code, setOwnCurrency]);
  const i18n = useLocaleSetting();

  const navigate = useNavigate();
  const { user, signOut } = useAuthStore();
  const general = usePlatformGeneral();
  const role = user?.role ?? 'PLATFORM_SUPPORT';

  const visible = NAV.filter((item) => !item.roles || role === 'SUPER_ADMIN' || item.roles.includes(role));

  // On a phone the rail is a drawer; a route change closes it so the next
  // tap lands on the page, not on the menu.
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [inboxOpen, setInboxOpen] = React.useState(false);
  const unreadInbox = usePlatformInboxUnread();
  const location = useLocation();
  React.useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <div className="flex min-h-screen bg-surface-variant">
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r border-outline bg-surface transition-transform duration-200',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <div className="flex h-14 items-center gap-2.5 border-b border-outline px-4">
          {general?.consoleBrandDisplay === 'logo' && brand.logo ? (
            // A wordmark carries the name itself, so it gets the width a square
            // mark does not need — squeezing one into 32px is what made it
            // unreadable.
            <img src={brand.logo} alt={general.siteName} style={{ height: logoHeight }} className="min-w-0 max-w-[168px] flex-1 object-contain object-left" />
          ) : (
            <>
              {general?.consoleBrandDisplay !== 'name' ? (
                <div style={{ width: logoHeight, height: logoHeight }} className="flex shrink-0 items-center justify-center overflow-hidden rounded-sk-md bg-sk-brand">
                  {brand.logo ? (
                    <img src={brand.logo} alt="" className="size-full object-contain" />
                  ) : (
                    <span className="font-display text-sm font-extrabold text-white">{general?.siteName.charAt(0)}</span>
                  )}
                </div>
              ) : null}
              <div className="min-w-0">
                <p className="truncate font-display text-sm font-bold">{general?.siteName}</p>
                <p className="truncate text-2xs text-ink-3">{t('Platform console')}</p>
              </div>
            </>
          )}
          <button type="button" onClick={() => setMobileOpen(false)} className="ml-auto rounded-sk-sm p-1 text-ink-3 hover:text-ink-1 lg:hidden" aria-label={t('Close menu')}>
            <X className="size-4" />
          </button>
        </div>

        <nav className="sk-scroll flex-1 overflow-y-auto p-2">
          <ul className="space-y-0.5">
            {visible.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/platform'}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2.5 rounded-sk-md px-2.5 py-2 text-[13px] font-medium transition-colors',
                      isActive
                        ? 'bg-indigo-soft font-semibold text-indigo'
                        : 'text-ink-2 hover:bg-surface-variant hover:text-ink-1',
                    )
                  }
                >
                  <item.icon className="size-[18px] shrink-0" aria-hidden />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="border-t border-outline p-3">
          <div className="flex items-center gap-2.5">
            {/* The whole block is the link to the account page — a 32px avatar
                on its own is too small a target. */}
            <NavLink
              to="/platform/profile"
              className="flex min-w-0 flex-1 items-center gap-2.5 rounded-sk-sm p-1 -m-1 hover:bg-surface-variant focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo/40"
            >
              <Avatar name={user?.name ?? 'Staff'} src={user?.avatarUrl ?? null} size={32} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold">{user?.name}</p>
                <p className="truncate text-2xs text-ink-3">
                  {role.replace(/_/g, ' ').toLowerCase()}
                </p>
              </div>
            </NavLink>
            <button
              type="button"
              onClick={() => {
                signOut();
                navigate('/platform/login');
              }}
              aria-label={t('Sign out')}
              className="rounded-sk-sm p-1.5 text-ink-3 hover:bg-surface-variant hover:text-ink-1"
            >
              <LogOut className="size-4" />
            </button>
          </div>

          {!user?.totpEnabled ? (
            // Two-factor is mandatory for SUPER_ADMIN and PLATFORM_SUPPORT
            //. Nagging here is intentional.
            <NavLink
              to="/platform/security"
              className="mt-2 flex items-center gap-1.5 rounded-sk-sm bg-warning/15 px-2 py-1.5 text-2xs font-semibold text-warning"
            >
              {t('Set up two-factor authentication')}
            </NavLink>
          ) : null}
        </div>
      </aside>

      {mobileOpen ? (
        <button type="button" aria-label={t('Close menu')} className="fixed inset-0 z-30 bg-ink-1/40 lg:hidden" onClick={() => setMobileOpen(false)} />
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col lg:pl-60">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-outline bg-surface/85 px-4 backdrop-blur lg:px-6">
          <button type="button" onClick={() => setMobileOpen(true)} className="lg:hidden" aria-label={t('Open menu')}>
            <Menu className="size-5 text-ink-2" />
          </button>
          <Badge tone="danger" className="shrink-0">{t('Platform')}</Badge>
          <p className="hidden min-w-0 truncate text-[13px] text-ink-2 md:block">
            {t('You can see every society\'s data here. Every action you take is recorded.')}
          </p>
          <div className="ml-auto flex items-center gap-1.5">
            {/* The public site is what this console edits, so it is one click
                away rather than something to remember the address of. Icon
                only, like the other header controls. */}
            <a
              href={import.meta.env['VITE_LANDING_URL'] ?? 'http://localhost:3000'}
              target="_blank"
              rel="noreferrer noopener"
              title={t('Visit site')}
              aria-label={t('Visit site')}
              className="inline-flex size-9 items-center justify-center rounded-sk-md text-ink-2 hover:bg-surface-variant hover:text-ink-1"
            >
              <ExternalLink className="size-[18px]" aria-hidden />
            </a>
            <Button
              variant="ghost"
              size="icon"
              aria-label={unreadInbox > 0 ? t('Notifications, {0} unread', [unreadInbox]) : t('Notifications')}
              onClick={() => setInboxOpen(true)}
            >
              <span className="relative inline-flex">
                <Bell className="size-[18px]" />
                {unreadInbox > 0 ? (
                  <span
                    aria-hidden
                    className="absolute -right-2.5 -top-2.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-danger px-1 font-num text-[9px] font-bold leading-none text-white ring-2 ring-surface"
                  >
                    {unreadInbox > 99 ? '99+' : unreadInbox}
                  </span>
                ) : null}
              </span>
            </Button>
            <CurrencySwitch className="hidden sm:inline-flex" />
            <LanguageSwitch locale={i18n.locale} enabled={i18n.enabled} className="hidden sm:inline-flex" />
            <span className="hidden sm:contents">
              <ThemeToggle />
              <FullscreenToggle />
            </span>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <Avatar name={user?.name ?? 'Staff'} size={28} />
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('Sign out')}
              onClick={() => {
                signOut();
                navigate('/platform/login');
              }}
            >
              <LogOut className="size-4" />
            </Button>
          </div>
        </header>

        <main className="min-w-0 flex-1 p-4 lg:p-6">
          <React.Fragment key={i18n.key}>
            <Outlet />
          </React.Fragment>
        </main>
        <PlatformInboxPanel open={inboxOpen} onOpenChange={setInboxOpen} />
      </div>
    </div>
  );
}
